import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { WalletBalance } from '@/components/wallet/WalletBalance';
import { useQuery } from '@tanstack/react-query';
import { chatAPI } from '@/lib/api/chat';
import { creatorSpaceApi } from '@/lib/api/creatorSpace';
import { useGlobalWebSocket } from '@/contexts/GlobalWebSocketContext';
import { useNotifications } from '@/contexts/NotificationContext';
import {
  Heart,
  Home,
  Users,
  MessageCircle,
  Zap,
  User,
  LogOut,
  Trophy,
  X,
  LogIn,
  UserPlus,
  Bell,
  Crown,
  Wallet,
  ShoppingBag,
  Shield,
  LayoutDashboard,
  Palette,
  DollarSign,
  Settings,
  HelpCircle
} from 'lucide-react';
import { useAdminSidebar } from '@/contexts/AdminSidebarContext';
import { useDesign } from '@/contexts/DesignContext';
import { HStack, VStack } from '@/components/core';


export function MobileHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const { t } = useTranslation(['common', 'admin']);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { isAdminRoute, toggle: toggleAdminSidebar } = useAdminSidebar();
  const { config } = useDesign();
  const { isConnected, subscribe } = useGlobalWebSocket();
  const [unreadOverride, setUnreadOverride] = useState<number | null>(null);
  const { unreadCount: unreadNotificationCount } = useNotifications();
  
  // Don't hide mobile navigation anymore - keep it visible in chat

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
  };

  // Chat-Sessions für Unread-Indikator laden (geteilt mit ChatList via Query-Key)
  // IMPORTANT: staleTime must match ChatList's staleTime (60s) to prevent refetch conflicts
  const { data: chatSessions = [], refetch: refetchChatSessions } = useQuery({
    queryKey: ['chat-sessions-all'],
    queryFn: async () => {
      // Load both normal and archived sessions for complete unread count
      const [normalRes, archivedRes] = await Promise.all([
        chatAPI.getSessions(0, 100, false),
        chatAPI.getSessions(0, 100, true)
      ]);
      const normal = (normalRes.data || []).map((s: any) => ({ ...s, is_archived: false }));
      const archived = (archivedRes.data || []).map((s: any) => ({ ...s, is_archived: true }));
      return [...normal, ...archived];
    },
    enabled: isAuthenticated,
    staleTime: 60000, // Consistent with ChatList
  });

  // Only count unread from non-archived sessions
  const unreadChatCount = Array.isArray(chatSessions)
    ? chatSessions.filter((s: any) => !s.is_archived).reduce((sum: number, s: any) => sum + (s?.unread_count || 0), 0)
    : 0;
  const displayedUnreadChatCount = unreadOverride ?? unreadChatCount;

  // Falls die Sessions aktualisiert wurden, Override zurücksetzen
  useEffect(() => {
    if (unreadOverride !== null) {
      setUnreadOverride(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatSessions]);

  // Live-Updates für Unread-Indikator via WebSocket
  useEffect(() => {
    if (!isAuthenticated || !isConnected) return;
    const unsubscribe = subscribe((data: any) => {
      if (
        data?.type === 'unread_count_update' ||
        data?.type === 'message' ||
        data?.type === 'character_message' ||
        data?.type === 'chat_message' ||
        data?.type === 'ai_response' ||
        data?.type === 'message_saved'
      ) {
        // Falls Session-Counts bereits mitkommen, sofortige Anzeige ohne Netzrunde
        if (data?.type === 'unread_count_update' && data?.session_counts && typeof data.session_counts === 'object') {
          try {
            const sum = Object.values<number>(data.session_counts as Record<string, number>).reduce((a, b) => a + (b || 0), 0);
            setUnreadOverride(sum);
          } catch {
            // ignore graceful
          }
        }
        // sanftes Refetch, um nicht zu aggressiv zu pollen
        setTimeout(() => { void refetchChatSessions(); }, 250);
      }
    });
    return () => { unsubscribe?.(); };
  }, [isAuthenticated, isConnected, subscribe, refetchChatSessions]);

  // Bei Tab-Fokus/Visibility Wechsel aktualisieren (failsafe)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        void refetchChatSessions();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [isAuthenticated, refetchChatSessions]);

  // Check if any creators exist (same logic as Desktop Navigation)
  const { data: creators = [] } = useQuery({
    queryKey: ['creators', 'count'],
    queryFn: () => creatorSpaceApi.getCreators(0, 1),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
  const hasCreators = creators.length > 0;

  const appNavIcons = isAuthenticated ? [
    { to: '/characters', icon: Users, label: t('common:navigation.characters') },
    { to: '/chats', icon: MessageCircle, label: t('common:navigation.chats') },
    ...(hasCreators ? [{ to: '/creator-space', icon: Crown, label: t('common:navigation.creators') }] : []),
    { to: '/swipe', icon: Heart, label: t('common:navigation.swipe') },
    { to: '/matches', icon: Zap, label: t('common:navigation.matches') },
  ] : [
    { to: '/', icon: Home, label: t('common:navigation.home') },
    { to: '/characters', icon: Users, label: t('common:navigation.characters') },
  ];

  // Kompakte Admin-Hauptnavigation für die mobile Bottom-Bar (ersetzt App-Icons im Admin-Bereich)
  const adminNavIcons = [
    { to: '/admin/dashboard', icon: LayoutDashboard, label: t('admin:navigation.dashboard') },
    { to: '/admin/users', icon: Users, label: t('admin:navigation.users') },
    { to: '/admin/characters', icon: Palette, label: t('admin:navigation.content') },
    { to: '/admin/billing', icon: DollarSign, label: t('admin:navigation.business') },
    { to: '/admin/settings', icon: Settings, label: t('admin:navigation.system') },
  ];

  return (
    <>
      {/* Mobile Bottom Navigation - Dauerhaft Sticky (nur bis md-Breakpoint)
          Optimiert für mobile Browser UI (Safari/Chrome address bar hide/show) */}
      <div
        className="md:hidden mobile-nav-fixed design-glass-mobile-nav"
        style={{
          paddingBottom: 'env(safe-area-inset-bottom)',
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          /* GPU-Beschleunigung für stabile Position bei Browser UI changes */
          transform: 'translate3d(0, 0, 0)',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          /* Verhindert ungewollte Interaktionen beim Scrollen */
          touchAction: 'manipulation',
        }}
      >
        <HStack align="center" className="px-token-xs py-token-sm h-16 max-w-full overflow-hidden">
          <HStack align="center" className="flex-shrink-0 overflow-hidden flex-1 justify-between">
            {/* Left - Logo */}
            <Link
              to={isAuthenticated ? "/characters" : "/"}
              className="flex items-center transition-colors flex-shrink-0"
              style={{ color: 'var(--color-primary-text)' }}
            >
              {(config?.branding?.mobile_logo_url || config?.branding?.logo_url) ? (
                <img
                  src={config.branding.mobile_logo_url || config.branding.logo_url}
                  alt={config?.branding?.site_title || 'Logo'}
                  className="h-8 w-auto rounded flex-shrink-0"
                />
              ) : (
                <div className="w-8 h-8 rounded-token-button flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(to right, var(--color-primary-gradient-start), var(--color-accent-text))' }}>
                  <Heart size={20} className="text-white" />
                </div>
              )}
            </Link>
  
            {/* Right - Alle Icons mit Divider */
            // Im Admin-Bereich: Admin-Menü Toggle + Admin-Navigationspunkte
            // Außerhalb Admin: App-Navigationspunkte
            }
            {/* Admin Menu Toggle (nur im Admin-Bereich) */}
            {isAdminRoute && (
              <button
                type="button"
                onClick={toggleAdminSidebar}
                className="p-1.5 rounded-token-button transition-all duration-200 text-muted hover:text-white hover:bg-white/5 flex-shrink-0"
                aria-label={t('common:navigation.adminMenu')}
              >
                <Shield size={22} className="flex-shrink-0" />
              </button>
            )}

            {/* Navigation Icons */}
            {(isAdminRoute ? adminNavIcons : appNavIcons).map((navItem) => {
              const Icon = navItem.icon;
              // Smart active detection: exact match for specific routes, prefix match for others
              const isActive = navItem.to === '/'
                ? location.pathname === '/'
                : location.pathname === navItem.to || location.pathname.startsWith(navItem.to + '/');

              return (
                <Link
                  key={navItem.to}
                  to={navItem.to}
                  className={`p-1.5 rounded-token-button transition-all duration-200 flex-shrink-0 ${
                    isActive
                      ? ''
                      : 'hover:bg-white/5'
                  }`}
                  style={{
                    color: isActive ? 'var(--color-nav-icon-active)' : 'var(--color-nav-text)',
                    backgroundColor: isActive ? 'color-mix(in srgb, var(--color-nav-icon-active) 20%, transparent)' : undefined
                  }}
                  title={navItem.label}
                >
                  <div className="relative">
                    <Icon size={22} className="flex-shrink-0" style={isActive ? { color: 'var(--color-nav-icon-active)' } : undefined} />
                    {navItem.to === '/chats' && displayedUnreadChatCount > 0 && (
                      <span
                        className="absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 px-0.5 rounded-full text-[9px] leading-[14px] font-bold text-center whitespace-nowrap"
                        style={{ background: 'var(--color-nav-badge-bg)', color: 'var(--color-nav-badge-text)' }}
                      >
                        {displayedUnreadChatCount > 99 ? '99+' : displayedUnreadChatCount}
                      </span>
                    )}
                  </div>
                </Link>
              );
            })}

            {/* Divider */}
            {/* <div className="w-px h-5 bg-white/20 mx-0.5 flex-shrink-0" /> */ }

            {/* User Actions - gleicher Abstand wie Navigation Icons */}
            {isAuthenticated ? (
              <>
                  {/* Notification Bell */}
                  <button
                    type="button"
                    className="p-1.5 rounded-token-button transition-all duration-200 text-muted hover:text-white hover:bg-white/5 flex-shrink-0"
                    aria-label={t('common:navigation.notifications')}
                    onClick={() => navigate('/notifications')}
                  >
                    <div className="relative">
                      <Bell size={22} className="flex-shrink-0" />
                      {unreadNotificationCount > 0 && (
                        <span
                          className="absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 px-0.5 rounded-full text-[9px] leading-[14px] font-bold text-center whitespace-nowrap"
                          style={{ background: 'var(--color-nav-badge-bg)', color: 'var(--color-nav-badge-text)' }}
                        >
                          {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                        </span>
                      )}
                    </div>
                  </button>

                {/* User Menu Toggle */}
                <div
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="p-1.5 rounded-token-button text-muted hover:text-white hover:bg-white/5 transition-all duration-200 cursor-pointer flex-shrink-0"
                >
                  <User size={22} className="flex-shrink-0" />
                </div>
              </>
            ) : (
              <>
                {/* Menu Toggle für nicht-eingeloggte Benutzer */}
                <div
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="p-1.5 rounded-token-button text-muted hover:text-white hover:bg-white/5 transition-all duration-200 cursor-pointer flex-shrink-0"
                >
                  <User size={22} className="flex-shrink-0" />
                </div>
              </>
            )}
          </HStack>
        </HStack>
      </div>

      {/* User Menu - Overlay (über der Navigation) */}
      {isUserMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
            onClick={() => setIsUserMenuOpen(false)}
          />

          {/* Menu Panel - Rechtsbündig auf Mobile */}
          <div className="fixed bottom-20 right-2 z-50 md:hidden">
            <div
              className="bg-background/95 backdrop-blur-md rounded-token-card border border-white/10 shadow-token-modal animate-slide-up max-h-[80vh] overflow-y-auto w-full max-w-xs"
              style={{
                backgroundColor: 'var(--glass-dropdown-bg)',
                backdropFilter: 'blur(var(--glass-dropdown-blur)) saturate(150%)',
                WebkitBackdropFilter: 'blur(var(--glass-dropdown-blur)) saturate(150%)'
              }}
            >
              {/* Header */}
              <HStack align="start" justify="between" className="p-token-md border-b border-white/10">
                <HStack align="center" gap="sm">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: 'linear-gradient(to right, var(--color-primary-gradient-start), var(--color-accent-text))' }}>
                    <User size={20} className="text-white" />
                  </div>
                  <VStack gap="none">
                    {isAuthenticated ? (
                      <>
                        <div className="text-white font-semibold">{user?.username}</div>
                        <WalletBalance />
                      </>
                    ) : (
                      <>
                        <div className="text-white font-semibold">{t('common:auth.guest')}</div>
                        <div className="text-muted text-sm">{t('common:auth.notLoggedIn')}</div>
                      </>
                    )}
                  </VStack>
                </HStack>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsUserMenuOpen(false)}
                  className="text-muted hover:text-white shrink-0"
                >
                  <X size={20} />
                </Button>
              </HStack>

              {/* Menu Items */}
              <VStack gap="xs" className="p-token-md">
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/profile"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center gap-token-sm p-token-sm rounded-token-button transition-colors hover:bg-white/5"
                      style={location.pathname === '/profile' ? { color: 'var(--color-nav-icon-active)', backgroundColor: 'color-mix(in srgb, var(--color-nav-icon-active) 20%, transparent)' } : { color: 'var(--color-nav-text)' }}
                    >
                      <User size={18} />
                      <span>{t('common:navigation.profile')}</span>
                    </Link>
                    <Link
                      to="/wallet"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center gap-token-sm p-token-sm rounded-token-button transition-colors hover:bg-white/5"
                      style={location.pathname === '/wallet' ? { color: 'var(--color-nav-icon-active)', backgroundColor: 'color-mix(in srgb, var(--color-nav-icon-active) 20%, transparent)' } : { color: 'var(--color-nav-text)' }}
                    >
                      <Wallet size={18} />
                      <span>{t('common:navigation.wallet')}</span>
                    </Link>
                    <Link
                      to="/subscription"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center gap-token-sm p-token-sm rounded-token-button transition-colors hover:bg-white/5"
                      style={location.pathname === '/subscription' || location.pathname === '/my-subscriptions' ? { color: 'var(--color-nav-icon-active)', backgroundColor: 'color-mix(in srgb, var(--color-nav-icon-active) 20%, transparent)' } : { color: 'var(--color-nav-text)' }}
                    >
                      <Crown size={18} />
                      <span>{t('common:navigation.mySubscriptions')}</span>
                    </Link>
                    <Link
                      to="/shop"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center gap-token-sm p-token-sm rounded-token-button transition-colors hover:bg-white/5"
                      style={location.pathname === '/shop' ? { color: 'var(--color-nav-icon-active)', backgroundColor: 'color-mix(in srgb, var(--color-nav-icon-active) 20%, transparent)' } : { color: 'var(--color-nav-text)' }}
                    >
                      <ShoppingBag size={18} />
                      <span>{t('common:navigation.shop')}</span>
                    </Link>
                    <Link
                      to="/daily-challenges"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center gap-token-sm p-token-sm rounded-token-button transition-colors hover:bg-white/5"
                      style={location.pathname === '/daily-challenges' ? { color: 'var(--color-nav-icon-active)', backgroundColor: 'color-mix(in srgb, var(--color-nav-icon-active) 20%, transparent)' } : { color: 'var(--color-nav-text)' }}
                    >
                      <Trophy size={18} />
                      <span>{t('common:navigation.dailyChallenges')}</span>
                    </Link>
                    <Link
                      to="/favorites"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center gap-token-sm p-token-sm rounded-token-button transition-colors hover:bg-white/5"
                      style={location.pathname === '/favorites' ? { color: 'var(--color-nav-icon-active)', backgroundColor: 'color-mix(in srgb, var(--color-nav-icon-active) 20%, transparent)' } : { color: 'var(--color-nav-text)' }}
                    >
                      <Heart size={18} />
                      <span>{t('common:navigation.favorites')}</span>
                    </Link>
                    <Link
                      to="/achievements"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center gap-token-sm p-token-sm rounded-token-button transition-colors hover:bg-white/5"
                      style={location.pathname === '/achievements' ? { color: 'var(--color-nav-icon-active)', backgroundColor: 'color-mix(in srgb, var(--color-nav-icon-active) 20%, transparent)' } : { color: 'var(--color-nav-text)' }}
                    >
                      <Trophy size={18} />
                      <span>{t('common:navigation.achievements')}</span>
                    </Link>
                    <Link
                      to="/support"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center gap-token-sm p-token-sm rounded-token-button transition-colors hover:bg-white/5"
                      style={location.pathname === '/support' ? { color: 'var(--color-nav-icon-active)', backgroundColor: 'color-mix(in srgb, var(--color-nav-icon-active) 20%, transparent)' } : { color: 'var(--color-nav-text)' }}
                    >
                      <HelpCircle size={18} />
                      <span>{t('common:navigation.support')}</span>
                    </Link>
                  </>
                ) : (
                  <>
                    <Link
                      to="/auth?tab=login"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center gap-token-sm p-token-sm rounded-token-button text-muted hover:text-white hover:bg-white/5 transition-colors"
                    >
                      <LogIn size={18} />
                      <span>{t('common:navigation.login')}</span>
                    </Link>
                    <Link
                      to="/auth?tab=register"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center gap-token-sm p-token-sm rounded-token-button transition-colors"
                      style={{ color: 'var(--color-nav-icon-active)', backgroundColor: 'color-mix(in srgb, var(--color-nav-icon-active) 20%, transparent)' }}
                    >
                      <UserPlus size={18} />
                      <span>{t('common:navigation.register')}</span>
                    </Link>
                  </>
                )}
              </VStack>

              {/* Footer Links */}
              <div className="p-token-md border-t border-white/10">
                <div className="flex items-center gap-token-sm p-token-sm text-xs text-muted">
                  <span className="w-[18px]" />
                  <span>{t('common:legal.title')}</span>
                </div>
                <VStack gap="xs">
                  <Link
                    to="/impressum"
                    onClick={() => setIsUserMenuOpen(false)}
                    className="flex items-center gap-token-sm p-token-sm rounded-token-button text-muted hover:text-white hover:bg-white/5 transition-colors"
                  >
                    <span className="w-[18px]" />
                    <span>{t('common:legal.impressum')}</span>
                  </Link>
                  <Link
                    to="/datenschutz"
                    onClick={() => setIsUserMenuOpen(false)}
                    className="flex items-center gap-token-sm p-token-sm rounded-token-button text-muted hover:text-white hover:bg-white/5 transition-colors"
                  >
                    <span className="w-[18px]" />
                    <span>{t('common:legal.privacy')}</span>
                  </Link>
                  <Link
                    to="/agb"
                    onClick={() => setIsUserMenuOpen(false)}
                    className="flex items-center gap-token-sm p-token-sm rounded-token-button text-muted hover:text-white hover:bg-white/5 transition-colors"
                  >
                    <span className="w-[18px]" />
                    <span>{t('common:legal.terms')}</span>
                  </Link>
                </VStack>
              </div>

              {/* Logout - nur für authentifizierte Benutzer */}
              {isAuthenticated && (
                <div className="p-token-md border-t border-white/10">
                  <Button
                    onClick={handleLogout}
                    variant="ghost"
                    className="w-full justify-start gap-token-sm p-token-sm hover:opacity-80"
                    style={{ color: 'var(--color-error)' }}
                  >
                    <LogOut size={18} />
                    <span>{t('common:navigation.logout')}</span>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
